import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Api from '../util/Api';
import { useHost } from '../hooks/use-host';
import { applyHostApiHost } from '../services/host';
import { setCurrency, setLocale } from '../util/Localize';
import { patchBrandThemeCaches } from '../util/brand-theme-cache';
import { setThemePersistedCallback, syncThemeFromBrand, updateIcon } from '../util/Theme';

/**
 * Syncs host/brand metadata (theme, locale, Api.hostInfo, favicon) from host.hd.pics.
 * `document.title` belongs to `RouteTitleAndPageView` (ROBO-932): it needs the route as well as the brand.
 */
export function HostEnvironment() {
    const queryClient = useQueryClient();
    const { data: hostInfo } = useHost();
    const hostThemeLoadedForBrand = useRef<number | null>(null);
    const appliedHostThemeRef = useRef('');

    useEffect(() => {
        setThemePersistedCallback(theme => {
            patchBrandThemeCaches(queryClient, theme, false);
        });
        return () => setThemePersistedCallback(undefined);
    }, [queryClient]);

    useEffect(() => {
        Api.hostInfo = hostInfo ?? {};

        applyHostApiHost(hostInfo);

        const hostTheme = hostInfo?.theme;
        if (hostInfo?.brandId != null && Array.isArray(hostTheme) && hostTheme.length > 0) {
            const themeKey = `${hostInfo.brandId}:${JSON.stringify(hostTheme)}`;
            if (appliedHostThemeRef.current !== themeKey) {
                appliedHostThemeRef.current = themeKey;
                if (hostThemeLoadedForBrand.current !== hostInfo.brandId) {
                    hostThemeLoadedForBrand.current = hostInfo.brandId;
                }
                // `syncThemeFromBrand` (not a bare `setTheme`) so it notifies
                // `subscribeThemeChange` listeners — e.g. the signed-out Login page, which reads
                // the welcome heading/body/button label straight from the theme cache (UX-800)
                // and has no other signal that this async host fetch has landed.
                syncThemeFromBrand(hostTheme);
            }
        }

        if (hostInfo?.culture) {
            setLocale(hostInfo.culture);
        }
        if (hostInfo?.currency) {
            setCurrency(hostInfo.currency);
        }
        if (hostInfo?.favIcon) {
            updateIcon();
        }
    }, [hostInfo]);

    return null;
}
